import classNames from "classnames";
import moment from "moment";
import React, { FC, ReactNode, useEffect, useState } from "react";
import {
  LuBuilding2,
  LuCheck,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuInbox,
  LuInfo,
  LuMail,
  LuUser,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getInitials,
  getInitialsFromFullName,
  showStatus,
} from "../../../constants/constants";
import { WorkModel } from "../../../factories/works/models/work.model";
import { useGetOrgQuery } from "../../../services/OrgService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useUpdateWorkRoleMutation } from "../../../slices/WorkSlice";
import { GetPageTitle } from "../../../support/ScrollToTop";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import { NotificationMiniList } from "../../notifications/NotificationMiniList";
import { useNotificationActions } from "../../notifications/useNotificationActions";
import {
  SelectBox,
  useWorkBulkActions,
  WorkBulkBar,
} from "../../works/board/WorkBulkActions";
import {
  allWorksSelected,
  keepListedSelection,
  toggleAllWorks,
  toggleSelectedWork,
} from "../../works/board/worksBoard.utils";
import { WorksArchiveConfirmModal } from "../../works/components/WorksArchiveConfirmModal";
import { StatusesDropdown } from "../../works/dropdown/StatusesDropdown";
import { getStatusColor } from "../../works/dropdown/utils";
import {
  attentionLabel,
  barShare,
  compareWithLast,
  daysBetween,
  Delta,
  greeting,
  HomeView,
  inboxTimeLabel,
  inDaysLabel,
  pipelinePage,
  workTypeColor,
} from "./homeDashboard.utils";
import { DailyBriefingView, useDailyBriefing } from "./briefing/DailyBriefing";
import {
  RECENT_EMAIL_DAYS,
  useClientInbox,
  useHomeDashboard,
} from "./useHomeDashboard";

const VIEW_KEY = "home-view";
/**
 * Needs attention today (4 rows) and Upcoming deadlines (3 rows) always have
 * the same fixed height, empty or not; longer lists scroll (owner, 2026-10-19).
 */
const ATTENTION_CARD_HEIGHT = "h-[340px]";
/**
 * Needs attention today: one column each for work name, contact, status,
 * assignee (wide screens) and due date, with titles (owner, Roadmap 1836).
 * Status and assignee columns also grow with the card, so the spare room is
 * shared out evenly instead of all going to work name / contact, which left
 * the status pill jammed against the assignee (Roadmap 1861).
 * First column = row checkbox for Change status / Archive (Roadmap 1875).
 */
const ATTENTION_COLUMNS =
  "grid items-center gap-x-5 grid-cols-[18px_minmax(0,1.2fr)_minmax(0,1fr)_minmax(178px,1fr)_64px] md:grid-cols-[18px_minmax(0,1.2fr)_minmax(0,1fr)_minmax(178px,1fr)_minmax(150px,1fr)_64px]";

const Card: FC<{ className?: string; children: ReactNode }> = ({
  className,
  children,
}) => (
  <div
    className={classNames(
      "rounded-[14px] border border-[#E8E8EC] bg-white",
      className,
    )}
  >
    {children}
  </div>
);

/** The explanation that used to be a subtitle, shown on hover / focus (Roadmap 1828). */
const InfoTip: FC<{ text: string }> = ({ text }) => (
  <span
    tabIndex={0}
    aria-label={text}
    className={
      "group relative inline-flex cursor-help rounded-full outline-none"
    }
  >
    <LuInfo
      size={14}
      strokeWidth={2}
      className={
        "text-[#A3A9B8] group-hover:text-[#6B4EFF] group-focus:text-[#6B4EFF]"
      }
    />
    <span
      role={"tooltip"}
      className={
        "pointer-events-none absolute left-1/2 top-full z-40 mt-2 hidden w-[260px] -translate-x-1/2 whitespace-normal rounded-lg bg-[#1F2433] px-3 py-2 text-left text-[12px] font-medium leading-[1.45] text-white shadow-lg group-hover:block group-focus:block"
      }
    >
      {text}
    </span>
  </span>
);

const CardTitle: FC<{ title: string; sub: string; right?: ReactNode }> = ({
  title,
  sub,
  right,
}) => (
  <div className={"flex items-start justify-between gap-3"}>
    <div
      className={
        "flex items-center gap-1.5 text-[15px] font-extrabold text-[#1F2433]"
      }
    >
      {title}
      <InfoTip text={sub} />
    </div>
    {right}
  </div>
);

const Empty: FC<{ title: string; text: string; icon?: ReactNode }> = ({
  title,
  text,
  icon,
}) => (
  <div
    className={
      "flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-8 text-center"
    }
  >
    <span
      className={
        "mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#E9F7EF] text-[#2E9E62]"
      }
    >
      {icon ?? <LuCheck size={20} />}
    </span>
    <div className={"text-[14px] font-bold text-[#1F2433]"}>{title}</div>
    <div className={"text-[12.5px] text-[#6B7185]"}>{text}</div>
  </div>
);

/** Status button of a "Needs attention" row (opens the status changer). */
const StatusPill: FC<{
  work: { jobStatus?: string | null; jobSubStatus?: string | null };
}> = ({ work }) => (
  <span
    className={
      "flex w-[178px] cursor-pointer items-center gap-2 rounded-lg border border-[#E6E8ED] bg-white px-[11px] py-[5px] text-[12.5px] font-semibold text-[#3A4256] hover:border-[#CFC6FF]"
    }
  >
    <span
      className={"h-2 w-2 shrink-0 rounded-full"}
      style={{ backgroundColor: getStatusColor(work.jobStatus || "") }}
    />
    <span className={"min-w-0 flex-1 truncate"}>{showStatus(work)}</span>
    <LuChevronDown
      size={12}
      strokeWidth={2.4}
      className={"shrink-0 text-[#9097A8]"}
    />
  </span>
);

const Avatar: FC<{ name: string; color?: string | null }> = ({
  name,
  color,
}) => {
  const [first = "", last = ""] = name.split(" ");
  return (
    <span
      className={
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
      }
      style={{ backgroundColor: color || "#1E3A8A" }}
    >
      {getInitials(first, last)}
    </span>
  );
};

/**
 * The client of a work item, clickable (opens the client; Roadmap 1837).
 * Same look as the Work page's Contact column: avatar + name, purple on hover.
 * It sits inside clickable rows, so it doesn't pass the click on.
 */
const ClientLink: FC<{
  work: Pick<WorkModel, "customerId" | "customerName" | "businessContactId">;
  avatar?: boolean;
  className?: string;
}> = ({ work, avatar = true, className }) => {
  const navigate = useNavigate();
  if (!work.customerName) return null;
  const open = () =>
    navigate(
      navigateToContact({
        type: work.businessContactId ? "organization" : "contact",
        id: work.businessContactId || "",
        customerId: work.customerId || "",
      }),
    );
  return (
    <span
      role={"link"}
      tabIndex={0}
      title={`Open ${work.customerName}`}
      onClick={(event) => {
        event.stopPropagation();
        open();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.stopPropagation();
        open();
      }}
      className={classNames(
        "group/client inline-flex min-w-0 max-w-full cursor-pointer items-center gap-2",
        className,
      )}
    >
      {avatar && (
        <span
          className={classNames(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white",
            work.businessContactId ? "bg-[#7E22CE]" : "bg-[#1E3A8A]",
          )}
        >
          {getInitialsFromFullName(work.customerName)}
        </span>
      )}
      <span
        className={
          "truncate group-hover/client:text-[#6B4EFF] group-hover/client:underline"
        }
      >
        {work.customerName}
      </span>
    </span>
  );
};

/** Props for a whole row that opens something (rows hold other clickables). */
const rowLink = (open: () => void) => ({
  role: "link" as const,
  tabIndex: 0,
  onClick: open,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && event.target === event.currentTarget) open();
  },
});

const StatCard: FC<{
  label: string;
  value?: number;
  tone?: "red" | "amber";
  delta?: Delta | null;
}> = ({ label, value, tone, delta }) => (
  <Card className={"flex min-h-[112px] flex-col px-[15px] pb-[14px] pt-[15px]"}>
    <div className={"h-4 text-right text-[11px] font-bold"}>
      {delta && (
        <span
          className={
            delta.tone === "good"
              ? "text-[#16A34A]"
              : delta.tone === "bad"
              ? "text-[#C0392B]"
              : "text-[#8A90A2]"
          }
        >
          {delta.text}
        </span>
      )}
    </div>
    <div
      className={classNames(
        "mt-auto text-[30px] font-extrabold leading-[30px]",
        tone === "red" && value
          ? "text-[#C0392B]"
          : tone === "amber" && value
          ? "text-[#B7791F]"
          : "text-[#1F2433]",
      )}
    >
      {value ?? "–"}
    </div>
    <div className={"mt-2 text-[12.5px] font-bold text-[#3A4256]"}>{label}</div>
  </Card>
);

const dueLabel = (work: WorkModel) => {
  const days = daysBetween(new Date(), new Date(work.dueDate));
  if (days < 0) return { text: attentionLabel(work.dueDate), late: true };
  if (days === 0) return { text: "Due today", late: true };
  return { text: `Due ${moment(work.dueDate).format("MMM D")}`, late: false };
};

/** Home page (V50): my work, or the whole firm for admins. */
export const HomeDashboard: FC = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: org } = useGetOrgQuery(orgId, { skip: !orgId });
  const [view, setView] = useState<HomeView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "firm" ? "firm" : "mine";
    } catch {
      return "mine";
    }
  });
  const board = useHomeDashboard(view);
  const inbox = useClientInbox();
  const isAdmin = board.user?.userRoleEnum?.userRole === "Admin";
  const firm = view === "firm" && isAdmin;
  const briefing = useDailyBriefing(firm ? "firm" : "my", board.user?.id);
  const [updateWorkRole] = useUpdateWorkRoleMutation();
  const notificationActions = useNotificationActions();
  const [pickingId, setPickingId] = useState<string | null>(null);
  // Ticked "Needs attention today" rows: Change status / Archive like the
  // Work page (Roadmap 1875; same bar, menu, confirm window and server calls).
  const [selected, setSelected] = useState<WorkModel[]>([]);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const bulk = useWorkBulkActions(selected, setSelected);
  const attentionList = board.attention;
  useEffect(() => {
    setSelected((current) => keepListedSelection(current, attentionList));
  }, [attentionList]);
  useEffect(() => setSelected([]), [view]);
  // Job status pipeline: which group of 6 statuses is shown (‹ ›).
  const [pipelinePageIndex, setPipelinePageIndex] = useState(0);
  useEffect(() => setPipelinePageIndex(0), [view]);
  const bulkFailed = () =>
    toast("Something went wrong. Please try again.", { type: "error" });

  useEffect(() => {
    document.title = GetPageTitle("Home");
  }, []);

  const switchView = (next: HomeView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Remembering the view is only a convenience.
    }
  };

  const openWork = (workId?: string | null) => {
    if (workId) navigate(`/work/${workId}`);
  };

  const pickUp = async (work: WorkModel) => {
    if (!board.user?.id) return;
    setPickingId(work.workId);
    try {
      await updateWorkRole({
        workId: work.workId,
        roleId: work.roleId,
        roleAssigneeId: board.user.id,
      }).unwrap();
      toast.success(`${work.workName} is now yours`);
    } catch {
      toast.error("Couldn't pick up this work. Please try again.");
    } finally {
      setPickingId(null);
    }
  };

  const today = moment().format("dddd, MMMM D, YYYY");
  const pipelineCounts = (board.pipeline ?? []).map((stage) => stage.count);
  const pipeline = pipelinePage(board.pipeline ?? [], pipelinePageIndex);
  const { cards } = board;

  return (
    <div className={"mx-auto w-full max-w-[1560px] font-Manrope"}>
      <div className={"mb-5 flex flex-wrap items-center justify-between gap-4"}>
        <div>
          <h1
            className={
              "flex items-center gap-2 text-[23px] font-extrabold text-[#1F2433]"
            }
          >
            {firm
              ? `${org?.legalName || "Your firm"} — firm overview`
              : `${greeting()}, ${
                  board.user?.userProfile?.firstName || "there"
                }`}
            <InfoTip
              text={
                firm
                  ? `Everyone in the firm · visible to admins only · ${today}`
                  : `Work assigned to you · ${today}`
              }
            />
          </h1>
        </div>
        {/* "Your day" collapsed line sits in the greeting row (owner 2026-11-01: don't push Home down). */}
        {!briefing.hidden && (
          <DailyBriefingView {...briefing.viewProps} part={"band"} />
        )}
        {isAdmin && (
          <div
            role={"tablist"}
            aria-label={"Home view"}
            className={"flex rounded-[10px] bg-[#F0F1F4] p-1"}
          >
            {(
              [
                ["mine", "My view", <LuUser key={"i"} size={14} />],
                ["firm", "Firm view", <LuBuilding2 key={"i"} size={14} />],
              ] as const
            ).map(([key, label, icon]) => (
              <button
                key={key}
                type={"button"}
                role={"tab"}
                aria-selected={view === key}
                onClick={() => switchView(key)}
                className={classNames(
                  "flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[13px] font-bold",
                  view === key
                    ? "bg-white text-[#1F2433] shadow-sm"
                    : "text-[#6B7185] hover:text-[#1F2433]",
                )}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!briefing.hidden && (
        <DailyBriefingView {...briefing.viewProps} part={"card"} />
      )}
      {briefing.composeModal}

      <Card className={"mb-4 p-5"}>
        <CardTitle
          title={"Job status pipeline"}
          sub={"Where active work sits right now. Click a stage to drill in."}
          right={
            pipeline.pages > 1 ? (
              <div
                className={"flex items-center gap-1.5"}
                aria-label={"More statuses"}
              >
                <span
                  className={"mr-1 text-[12px] font-semibold text-[#8A90A2]"}
                >
                  {pipeline.page + 1} of {pipeline.pages}
                </span>
                {(
                  [
                    [
                      "Previous statuses",
                      -1,
                      pipeline.hasPrevious,
                      LuChevronLeft,
                    ],
                    ["More statuses", 1, pipeline.hasNext, LuChevronRight],
                  ] as const
                ).map(([label, step, enabled, Icon]) => (
                  <button
                    key={label}
                    type={"button"}
                    aria-label={label}
                    title={label}
                    disabled={!enabled}
                    onClick={() => setPipelinePageIndex(pipeline.page + step)}
                    className={
                      "flex h-7 w-7 items-center justify-center rounded-[8px] border border-[#DCE2EA] bg-white text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF] disabled:cursor-default disabled:opacity-40 disabled:hover:border-[#DCE2EA] disabled:hover:text-[#3A4256]"
                    }
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            ) : undefined
          }
        />
        <div
          className={
            "mt-4 grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3 xl:grid-cols-6"
          }
        >
          {board.pipeline && !board.pipeline.length && (
            <div className={"col-span-full text-[12.5px] text-[#8A90A2]"}>
              No active work yet.
            </div>
          )}
          {pipeline.stages.map((stage) => (
            <button
              key={stage.key}
              type={"button"}
              title={
                stage.group
                  ? `${stage.label} (${stage.group})`
                  : "All other statuses"
              }
              onClick={() =>
                navigate(
                  `/works/${firm ? "all" : "my"}${
                    stage.group
                      ? `?status=${encodeURIComponent(stage.group)}`
                      : ""
                  }`,
                )
              }
              className={"group min-w-0 text-left"}
            >
              <div className={"flex items-center justify-between gap-2"}>
                <span
                  className={
                    "flex min-w-0 items-center gap-2 text-[12.5px] font-bold text-[#3A4256] group-hover:text-[#6B4EFF]"
                  }
                >
                  <span
                    className={"h-2 w-2 shrink-0 rounded-full"}
                    style={{ backgroundColor: stage.color }}
                  />
                  <span className={"truncate"}>{stage.label}</span>
                </span>
                <span className={"text-[18px] font-extrabold text-[#1F2433]"}>
                  {stage.count}
                </span>
              </div>
              <div
                className={
                  "mt-2 h-[6px] overflow-hidden rounded-full bg-[#EEF0F4]"
                }
              >
                <div
                  className={"h-full rounded-full"}
                  style={{
                    width: `${barShare(stage.count, pipelineCounts)}%`,
                    backgroundColor: stage.color,
                  }}
                />
              </div>
            </button>
          ))}
        </div>
      </Card>

      <div
        className={"mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5"}
      >
        <StatCard label={"Overdue"} value={cards.overdue} tone={"red"} />
        <StatCard
          label={"Due today"}
          value={cards.dueToday}
          tone={"amber"}
          delta={compareWithLast(cards.dueToday, cards.dueTodayLast)}
        />
        <StatCard
          label={"Due this week"}
          value={cards.dueWeek}
          delta={compareWithLast(cards.dueWeek, cards.dueWeekLast)}
        />
        <StatCard label={"Active jobs"} value={cards.active} />
        <StatCard
          label={"Completed this week"}
          value={cards.completedWeek}
          delta={compareWithLast(cards.completedWeek, cards.completedLastWeek, {
            moreIsBetter: true,
            percent: true,
          })}
        />
      </div>

      <div
        className={
          "mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_420px]"
        }
      >
        <Card
          className={classNames(
            "flex min-w-0 flex-col p-5",
            ATTENTION_CARD_HEIGHT,
          )}
        >
          <CardTitle
            title={"Needs attention today"}
            sub={
              firm
                ? "Overdue first, then due today — across everyone in the firm."
                : "Your overdue work first, then what you have due today."
            }
            right={
              selected.length ? (
                <div className={"-my-1.5"}>
                  <WorkBulkBar
                    compact
                    count={selected.length}
                    busy={bulk.isBusy}
                    onChangeStatus={(status) =>
                      void bulk.changeStatus(status).catch(bulkFailed)
                    }
                    onArchive={() => setIsArchiveOpen(true)}
                    onClear={() => setSelected([])}
                  />
                </div>
              ) : undefined
            }
          />
          {board.attention && !board.attention.length ? (
            <Empty
              title={"Nothing needs attention"}
              text={"No overdue jobs and nothing due today."}
            />
          ) : (
            <>
              <div className={"mt-3 border-b border-[#ECEEF2] pr-2"}>
                <div
                  className={classNames(
                    ATTENTION_COLUMNS,
                    "px-2 pb-2 text-[11px] font-bold uppercase tracking-[0.04em] text-[#9097A8]",
                  )}
                >
                  <SelectBox
                    checked={allWorksSelected(board.attention ?? [], selected)}
                    label={
                      allWorksSelected(board.attention ?? [], selected)
                        ? "Unselect all"
                        : "Select all"
                    }
                    onChange={() =>
                      setSelected(
                        toggleAllWorks(board.attention ?? [], selected),
                      )
                    }
                  />
                  <span>Work name</span>
                  <span>Contact</span>
                  <span>Status</span>
                  <span className={"hidden md:block"}>Assignee</span>
                  <span className={"text-right"}>Due date</span>
                </div>
              </div>
              <div
                className={
                  "visible-scrollbar min-h-0 flex-1 overflow-y-auto pr-2"
                }
              >
                {(board.attention ?? []).map((work) => {
                  const late =
                    daysBetween(new Date(work.dueDate), new Date()) > 0;
                  const assignee = work.assigneeList?.[0];
                  const isSelected = selected.some(
                    (item) => item.workId === work.workId,
                  );
                  return (
                    <div
                      key={work.workId}
                      {...rowLink(() => openWork(work.workId))}
                      className={classNames(
                        ATTENTION_COLUMNS,
                        "group cursor-pointer rounded-lg border-b border-[#F0F1F4] px-2 py-[9px] last:border-b-0 hover:bg-[#F7F8FA]",
                        isSelected && "bg-[#FAF9FF]",
                      )}
                    >
                      <SelectBox
                        checked={isSelected}
                        label={`${isSelected ? "Unselect" : "Select"} ${
                          work.workName
                        }`}
                        onChange={() =>
                          setSelected(toggleSelectedWork(selected, work))
                        }
                      />
                      <span
                        className={
                          "min-w-0 truncate text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
                        }
                      >
                        {work.workName}
                      </span>
                      <span className={"flex min-w-0"}>
                        <ClientLink
                          work={work}
                          className={"text-[13px] font-semibold text-[#3A4256]"}
                        />
                      </span>
                      {!work.jobStatus ? (
                        <span />
                      ) : (
                        // The status menu changes the status; it doesn't open the work.
                        <span onClick={(event) => event.stopPropagation()}>
                          <StatusesDropdown
                            workTypeId={work.workTypeId || ""}
                            workId={work.workId || ""}
                            menuLook
                            current={{
                              statusId: work.jobStatusId,
                              subStatusId: work.jobSubStatusId,
                              statusName: work.jobStatus,
                              subStatusName: work.jobSubStatus,
                            }}
                            triggerElement={<StatusPill work={work} />}
                            renderUpdatedTrigger={(updated) => (
                              <StatusPill work={updated} />
                            )}
                          />
                        </span>
                      )}
                      <span
                        className={
                          "hidden min-w-0 items-center gap-2 text-[12px] font-semibold text-[#5F758F] md:flex"
                        }
                      >
                        {assignee && (
                          <>
                            <Avatar
                              name={`${assignee.firstName} ${assignee.lastName}`}
                              color={assignee.color}
                            />
                            <span className={"truncate"}>
                              {assignee.firstName} {assignee.lastName}
                            </span>
                          </>
                        )}
                      </span>
                      <span
                        className={
                          "text-right text-[12.5px] font-semibold text-[#5F758F]"
                        }
                      >
                        {late ? moment(work.dueDate).format("MMM D") : "Today"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>

        <Card
          className={classNames(
            "flex min-w-0 flex-col p-5",
            ATTENTION_CARD_HEIGHT,
          )}
        >
          <CardTitle
            title={"Upcoming deadlines"}
            sub={"Tomorrow through 7 days, by filing type."}
          />
          {board.upcoming && !board.upcoming.length ? (
            <Empty
              title={"Nothing coming up"}
              text={"Nothing is due in the next 7 days."}
            />
          ) : (
            <div
              className={
                "visible-scrollbar mt-3 min-h-0 flex-1 overflow-y-auto pr-2"
              }
            >
              {(board.upcoming ?? []).map((work) => (
                <div
                  key={work.workId}
                  {...rowLink(() => openWork(work.workId))}
                  className={
                    "flex w-full cursor-pointer gap-3 border-b border-[#F0F1F4] py-[11px] text-left last:border-b-0 hover:bg-[#F7F8FA]"
                  }
                >
                  <span
                    className={"w-[3px] shrink-0 rounded-full"}
                    style={{ backgroundColor: workTypeColor(work.workType) }}
                  />
                  <span className={"min-w-0 flex-1"}>
                    <span
                      className={"block truncate text-[12px] font-extrabold"}
                      style={{ color: workTypeColor(work.workType) }}
                    >
                      {work.workType}
                    </span>
                    <span className={"flex min-w-0"}>
                      <ClientLink
                        work={work}
                        avatar={false}
                        className={"text-[13.5px] font-bold text-[#1F2433]"}
                      />
                    </span>
                    <span
                      className={"block truncate text-[11.5px] text-[#8A90A2]"}
                    >
                      {work.workName}
                    </span>
                  </span>
                  <span className={"shrink-0 text-right"}>
                    <span
                      className={"block text-[12px] font-bold text-[#5F758F]"}
                    >
                      {inDaysLabel(work.dueDate)}
                    </span>
                    <span className={"block text-[11px] text-[#A3A9B8]"}>
                      {moment(work.dueDate).format("ddd")}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className={"grid grid-cols-1 gap-4 lg:grid-cols-3"}>
        <Card className={"flex h-[460px] flex-col"}>
          <div className={"border-b border-[#F0F1F4] px-5 pb-3 pt-[18px]"}>
            <CardTitle
              title={"Work I can pick"}
              sub={"Queued work in roles you belong to."}
              right={
                <span
                  className={
                    "rounded-full bg-[#F0EDFF] px-2 py-0.5 text-[11px] font-bold text-[#6B4EFF]"
                  }
                >
                  {board.canPick.length}
                </span>
              }
            />
          </div>
          <div className={"flex min-h-0 flex-1 flex-col overflow-y-auto px-5"}>
            {!board.isLoadingQueued && !board.canPick.length ? (
              <Empty
                title={"Nothing to pick up"}
                text={"No queued work in your roles right now."}
              />
            ) : (
              board.canPick.map((work) => {
                const due = dueLabel(work);
                return (
                  <div
                    key={work.workId}
                    {...rowLink(() => openWork(work.workId))}
                    className={
                      "group -mx-2 flex cursor-pointer items-center gap-3 border-b border-[#F0F1F4] px-2 py-3 last:border-b-0 hover:bg-[#F7F8FA]"
                    }
                  >
                    <span className={"min-w-0 flex-1 text-left"}>
                      <span
                        className={
                          "block truncate text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
                        }
                      >
                        {work.workName}
                      </span>
                      <span className={"flex min-w-0"}>
                        <ClientLink
                          work={work}
                          avatar={false}
                          className={"text-[12px] text-[#6B7185]"}
                        />
                      </span>
                      <span
                        className={"mt-1 flex items-center gap-2 text-[11px]"}
                      >
                        {work.roleName && (
                          <span
                            className={
                              "rounded-[5px] border border-[#DED6FB] bg-[#F0EDFF] px-[7px] py-px font-bold text-[#5B45D6]"
                            }
                          >
                            {work.roleName}
                          </span>
                        )}
                        <span
                          className={
                            due.late
                              ? "font-semibold text-[#C0392B]"
                              : "text-[#6B7185]"
                          }
                        >
                          {due.text}
                        </span>
                      </span>
                    </span>
                    <button
                      type={"button"}
                      disabled={pickingId === work.workId}
                      onClick={(event) => {
                        event.stopPropagation();
                        void pickUp(work);
                      }}
                      className={
                        "shrink-0 rounded-[8px] border border-[#DCD4FF] px-3 py-1.5 text-[12px] font-semibold text-[#6B4EFF] hover:bg-[#F4F2FE] disabled:opacity-50"
                      }
                    >
                      Pick up
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        <Card className={"flex h-[460px] flex-col"}>
          <div className={"border-b border-[#F0F1F4] px-5 pb-3 pt-[18px]"}>
            <CardTitle
              title={"Notifications"}
              sub={"Your most recent notifications."}
              right={
                <button
                  type={"button"}
                  onClick={() => navigate("/notifications")}
                  className={"text-[12px] font-semibold text-[#6B4EFF]"}
                >
                  View all
                </button>
              }
            />
          </div>
          <div className={"flex min-h-0 flex-1 flex-col overflow-y-auto px-5"}>
            {!board.isLoadingNotifications && !board.notifications.length ? (
              <Empty
                title={"You're all caught up"}
                text={"New notifications will show up here."}
              />
            ) : (
              <NotificationMiniList
                items={board.notifications}
                onOpen={notificationActions.open}
              />
            )}
          </div>
        </Card>

        <Card className={"flex h-[460px] flex-col"}>
          <div className={"border-b border-[#F0F1F4] px-5 pb-3 pt-[18px]"}>
            <CardTitle
              title={"Recent Client Emails"}
              sub={`Your clients’ emails from the last ${RECENT_EMAIL_DAYS} days. Older emails are on the Email page.`}
            />
          </div>
          <div className={"flex min-h-0 flex-1 flex-col overflow-y-auto px-5"}>
            {inbox.notConnected ? (
              <div
                className={
                  "flex flex-1 flex-col items-center justify-center gap-1.5 px-4 text-center"
                }
              >
                <span
                  className={
                    "mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EDFF] text-[#6B4EFF]"
                  }
                >
                  <LuMail size={20} />
                </span>
                <div className={"text-[14px] font-bold text-[#1F2433]"}>
                  Connect your mailbox
                </div>
                <div className={"text-[12.5px] text-[#6B7185]"}>
                  See your clients&apos; emails here once your email is
                  connected.
                </div>
                <button
                  type={"button"}
                  onClick={() => navigate("/settings/integrations")}
                  className={
                    "mt-2 rounded-[8px] bg-[#6B4EFF] px-3.5 py-2 text-[12.5px] font-bold text-white"
                  }
                >
                  Connect email
                </button>
              </div>
            ) : !inbox.isLoading && !inbox.emails.length ? (
              <Empty
                icon={<LuInbox size={20} />}
                title={
                  inbox.failed ? "Inbox unavailable" : "No recent client emails"
                }
                text={
                  inbox.failed
                    ? "Your emails couldn't be loaded right now."
                    : `Emails from your clients in the last ${RECENT_EMAIL_DAYS} days will show up here.`
                }
              />
            ) : (
              inbox.emails.map((email) => {
                const sender =
                  email.from?.[0]?.name || email.from?.[0]?.email || "Client";
                return (
                  <button
                    key={email.id}
                    type={"button"}
                    onClick={() => navigate("/mail")}
                    className={
                      "flex w-full gap-3 border-b border-[#F0F1F4] py-3 text-left last:border-b-0 hover:bg-[#F7F8FA]"
                    }
                  >
                    <span
                      className={
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1E3A8A] text-white"
                      }
                    >
                      <LuUser size={13} />
                    </span>
                    <span className={"min-w-0 flex-1"}>
                      <span
                        className={"flex items-baseline justify-between gap-2"}
                      >
                        <span
                          className={classNames(
                            "truncate text-[13px] text-[#1F2433]",
                            email.unread ? "font-extrabold" : "font-bold",
                          )}
                        >
                          {sender}
                        </span>
                        <span className={"shrink-0 text-[11px] text-[#A3A9B8]"}>
                          {inboxTimeLabel(email.date)}
                        </span>
                      </span>
                      <span
                        className={classNames(
                          "block truncate text-[13px] text-[#1F2433]",
                          email.unread ? "font-bold" : "font-semibold",
                        )}
                      >
                        {email.subject || "(no subject)"}
                      </span>
                      <span
                        className={"block truncate text-[12px] text-[#8A90A2]"}
                      >
                        {(email.snippet || "").replace(/<[^>]*>/g, "")}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </Card>
      </div>

      <WorksArchiveConfirmModal
        isOpen={isArchiveOpen}
        selectedWorks={selected}
        isLoading={bulk.isBusy}
        onClose={() => setIsArchiveOpen(false)}
        onConfirm={async () => {
          try {
            await bulk.archive(true);
          } catch {
            bulkFailed();
          }
          setIsArchiveOpen(false);
        }}
      />
    </div>
  );
};
